package co.uk.paxes.pinpoint.web

import co.uk.paxes.pinpoint.model.*
import co.uk.paxes.pinpoint.engine.GameEngine
import co.uk.paxes.pinpoint.engine.GameEngineListener
import co.uk.paxes.pinpoint.util.SeededRandom
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.*
import org.w3c.dom.events.MouseEvent
import kotlin.js.JsExport
import kotlin.js.JsName

/**
 * Player configuration for local game setup.
 */
data class PlayerConfig(
    val type: String,  // "human" or "ai"
    val name: String,
    val difficulty: String = "easy"
)

/**
 * Manages local multiplayer games (2-4 players, mix of human and AI).
 */
object LocalGameManager {
    
    // Game state
    private var gameEngine: GameEngine? = null
    private var playerConfigs: List<PlayerConfig> = emptyList()
    private var isGameOver: Boolean = false
    private var currentHumanPlayerId: String? = null  // The human whose turn it is (for pass-and-play)
    
    // Hand state
    private var selectedPinIndex: Int = 0
    private var pendingJokerIndex: Int? = null
    private var pendingJokerPin: Pin? = null
    
    // Undo state - History Stack
    // Data classes moved outside object to avoid serialization issues
    // private data class UndoSnapshot moved to file level
    
    // Undo credits are managed by RewardedAdManager
    // private var undoCredits: Int = 0
    private val undoStack = mutableListOf<UndoSnapshot>()
    
    // Hand scores tracking
    private var playerHandScores: MutableMap<String, Int> = mutableMapOf()
    private val scoreCalculator = ScoreCalculator()
    private var previousHandSize: Int = 4  // Track hand size to detect new hand

    // AI Opponents map
    private var aiOpponents: MutableMap<String, AIPlayer> = mutableMapOf()
    
    // Flag to track if we're currently restoring a game (to skip premature game-over checks)
    private var isRestoringGame = false

    // Score tracking for display
    private var lastPinScore: Int? = null
    private var lastPinScorePlayerId: String? = null
    private var turnTransitionTimeoutId: Int? = null // To cancel turn switch on undo

    
    // Score Flash Animation state
    private var flashingPositions: Set<Position> = emptySet()
    private var isFlashVisible: Boolean = false
    private var scoreFlashIntervalId: Int? = null
    private var scoreFlashTimeoutId: Int? = null
    
    // Interstitial ad tracking
    private var handsSinceLastInterstitial = 0
    private var totalInterstitialsShown = 0
    private val INTERSTITIAL_HAND_INTERVALS = listOf(4, 8)  // Show ads after 4th and 8th hand
    private const val MAX_INTERSTITIALS_DURING_GAME = 2
    
    // Game pause state
    private var isGamePaused = false
    private var pendingAITimeoutId: Int? = null
    
    // Pin colors for rendering
    private val PIN_COLORS = mapOf(
        PinColor.RED to "#FF6B6B",
        PinColor.GREEN to "#00FF00",
        PinColor.PURPLE to "#8B00FF",
        PinColor.YELLOW to "#FFFF00",
        PinColor.BLACK to "#000000"
    )
    
    /**
     * Get the difficulty emoji based on AI player difficulty.
     */
    private fun getDifficultyEmoji(playerId: String): String {
        val aiPlayer = aiOpponents[playerId]
        return when (aiPlayer?.difficulty?.uppercase()) {
            "EASY" -> "😊"
            "MEDIUM" -> "🤔"
            "HARD" -> "😤"
            "EXPERT" -> "🧠"
            else -> "🤖"
        }
    }
    
    /**
     * Initialize the local game manager.
     */
    fun initialize() {
        // Initialize undo credits if not already present
        val currentCredits = co.uk.paxes.pinpoint.web.RewardedAdManager.getUndoCredits()
        console.log("LocalGameManager.initialize(): Current undo credits: $currentCredits")
        
        // Initialize banner ads
        co.uk.paxes.pinpoint.web.BannerAdManager.initialize()
        
        // Export functions to window
        val w = window.asDynamic()
        w.startLocalGameWithConfig = { configJson: String -> startGameWithConfig(configJson) }
        w.quitLocalGame = { quitGame() }
        w.confirmPassPlay = { confirmPassPlay() }
        w.selectLocalHandPin = { i: Int -> LocalGameManager.selectHandPin(i) }
        w.localSacrificePinForJoker = { i: Int -> LocalGameManager.sacrificePinForJoker(i) }
        w.localSelectJokerColor = { c: String -> LocalGameManager.selectJokerColor(c) }
        w.localCancelJoker = { LocalGameManager.cancelJoker() }
        w.localRequestUndo = { LocalGameManager.requestUndo() }
        w.localWatchAdForCredit = { LocalGameManager.watchAdForCredit() }
        
        console.log("LocalGameManager initialized")
    }

    
    /**
     * Start a local game with the given player configurations.
     */
    fun startGameWithConfig(configJson: String) {
        console.log("LocalGameManager: Starting game with config: $configJson")
        
        try {
            // Parse JSON using JavaScript
            val parsed = JSON.parse<Array<dynamic>>(configJson)
            playerConfigs = parsed.map { item ->
                PlayerConfig(
                    type = item.type as String,
                    name = item.name as String,
                    difficulty = (item.difficulty as? String) ?: "easy"
                )
            }
        } catch (e: Exception) {
            console.error("Failed to parse player config: ${e.message}")
            window.alert("Game Error: Failed to parse config: ${e.message}")
            return
        }
        
        if (playerConfigs.size < 2 || playerConfigs.size > 4) {
            console.error("Invalid player count: ${playerConfigs.size}")
            return
        }
        
        isGameOver = false
        selectedPinIndex = 0
        undoStack.clear()
        
        // Reset score tracking
        lastPinScore = null
        lastPinScorePlayerId = null
        playerHandScores.clear()
        previousHandSize = 4
        
        // CRITICAL: Reset ad counters for new game
        handsSinceLastInterstitial = 0
        totalInterstitialsShown = 0
        
        // Clear any pending transition
        turnTransitionTimeoutId?.let { window.clearTimeout(it) }
        turnTransitionTimeoutId = null
        
        // Create players and AI opponents
        val players = mutableListOf<Player>()
        val aiMap = mutableMapOf<String, AIPlayer>()
        
        playerConfigs.forEachIndexed { index, config ->
            val playerId = "player-$index"
            val isAI = config.type == "ai"
            val playerName = config.name // Don't add emoji here - let display logic handle it
            val player = Player(id = playerId, name = playerName, isAI = isAI)
            players.add(player)
            
            if (config.type == "ai") {
                val difficulty = when (config.difficulty.lowercase()) {
                    "easy" -> AIDifficulty.EASY
                    "medium" -> AIDifficulty.MEDIUM
                    "hard" -> AIDifficulty.HARD
                    "expert" -> AIDifficulty.EXPERT
                    else -> AIDifficulty.EASY
                }
                aiMap[playerId] = AIPlayer(
                    id = playerId,
                    name = config.name,
                    difficulty = difficulty.toString()
                )
            }
        }
        
        aiOpponents = aiMap
        
        // Create game config
        val seed = kotlin.js.Date.now().toLong()
        val config = GameConfig(
            players = players,
            startingPlayerId = players.first().id,
            boardSeed = seed,
            isNetworkClient = false
        )
        
        // Create game engine
        val engine = GameEngine(listener = object : GameEngineListener {
            override fun onGameStarted(state: GameState) {
                console.log("LocalGameManager: Game started")
                autoSaveGameState()
                handleTurnStart(state)
            }
            
            override fun onMoveApplied(state: GameState, move: Move) {
                console.log("LocalGameManager: Move applied by ${move.playerId}, hand size: ${state.currentHand.size}")
                
                // key logic: Detect if a NEW hand was just dealt.
                // A new hand has size 4, and the previous hand (before this move + deal) had <= 1 pin.
                val isNewHand = state.currentHand.size == 4 && previousHandSize <= 1
                
                if (isNewHand) {
                    // New hand dealt - reset hand scores for the new player
                    val newPlayerId = state.currentPlayerId
                    if (newPlayerId != null) {
                        console.log("LocalGameManager: New hand dealt for $newPlayerId, resetting hand score")
                        playerHandScores[newPlayerId] = 0
                    }
                    
                    // DO NOT trigger H5AdManager - use our PlayGama ads instead
                    // H5AdManager.triggerNextBreak("hand_complete")
                    
                    // Track hands for interstitial ads
                    handsSinceLastInterstitial++
                    console.log("🎯 LocalGameManager: Hand $handsSinceLastInterstitial completed")
                    console.log("🎯 Total ads shown so far: $totalInterstitialsShown/$MAX_INTERSTITIALS_DURING_GAME")
                    console.log("🎯 Should show ad at hands: ${INTERSTITIAL_HAND_INTERVALS.joinToString()}")
                    console.log("🎯 Current hand in interval list? ${INTERSTITIAL_HAND_INTERVALS.contains(handsSinceLastInterstitial)}")
                    console.log("🎯 Can show more ads? ${totalInterstitialsShown < MAX_INTERSTITIALS_DURING_GAME}")
                    
                    // Show ad at specific hand intervals (4th, 8th)
                    if (INTERSTITIAL_HAND_INTERVALS.contains(handsSinceLastInterstitial) && totalInterstitialsShown < MAX_INTERSTITIALS_DURING_GAME) {
                        console.log("🎬 LocalGameManager: TRIGGERING interstitial after hand $handsSinceLastInterstitial")
                        totalInterstitialsShown++
                        
                        // Show the ad - BannerAdManager will call onAdShowing when ad appears
                        BannerAdManager.showInterstitialAd("interstitial_hand_$handsSinceLastInterstitial", 
                            onAdShowing = {
                                console.log("⏸️ LocalGameManager: Ad is showing - pausing game")
                                isGamePaused = true
                            },
                            onClose = {
                                console.log("✅ LocalGameManager: Ad closed - resuming game")
                                isGamePaused = false
                            }
                        )
                    } else {
                        console.log("⏭️ LocalGameManager: Skipping ad - not at interval or max reached")
                    }
                    
                    // LEGACY: Granting free credits removed to enforce ad-based system
                }
                
                // Update tracker for next time
                previousHandSize = state.currentHand.size
                
                // Check for game over
                if (state.board.isFull()) {
                    handleGameOver(state)
                } else {
                    // If a hand was just completed, add a 3-second pause so results can be seen and UNDO is possible
                    if (isNewHand) {
                        console.log("LocalGameManager: Hand completed, pausing for 3 seconds...")
                        // Store ID so we can cancel it if Undo is pressed during this window
                        turnTransitionTimeoutId = window.setTimeout({
                            turnTransitionTimeoutId = null // Clear ID when executed
                            console.log("LocalGameManager: Undo pause ended, continuing to next turn")
                            // Reset pin score now that the pause is over and we're switching turns
                            lastPinScore = null
                            lastPinScorePlayerId = null
                            handleTurnStart(state)
                        }, 3000)
                    } else {
                        handleTurnStart(state)
                    }
                }
            }
            
            override fun onGameEnded(state: GameState) {
                console.log("LocalGameManager: Game ended")
                handleGameOver(state)
            }
        })
        
        gameEngine = engine
        try {
            engine.startGame(config)
        } catch (e: Exception) {
            console.error("FATAL: Failed to start game engine: ${e.message}")
            window.alert("Game Error: Failed to start game: ${e.message}")
        }
    }
    
    /**
     * Handle the start of a turn - check if AI or human.
     */
    private fun handleTurnStart(state: GameState) {
        val currentPlayerId = state.currentPlayerId ?: return
        val aiOpponent = aiOpponents[currentPlayerId]
        
        // Update TurnBasedUIManager for button visibility
        val isHumanTurn = aiOpponent == null
        TurnBasedUIManager.updateButtonVisibility(isHumanTurn)
        console.log("LocalGameManager: Updated button visibility - isHumanTurn=$isHumanTurn")
        
        // Don't reset pin score here - it was already reset in the timeout
        // This prevents double-resetting and ensures the score shows during the pause
        
        if (aiOpponent != null) {
            // AI's turn - make move automatically
            console.log("LocalGameManager: AI turn for ${aiOpponent.name}")
            currentHumanPlayerId = null
            
            // Show the game screen with AI playing indicator
            if (NavigationManager.getCurrentScreen() != Screen.LOCAL_GAME) {
                NavigationManager.navigateTo(Screen.LOCAL_GAME)
                window.setTimeout({ setupBoardClickHandler() }, 100)
            }
            
            renderAll(state)
            scheduleAIMove(aiOpponent, state)
        } else {
            // Human's turn - check if we need pass-and-play
            val previousHuman = currentHumanPlayerId
            currentHumanPlayerId = currentPlayerId
            
            // If different human than before, show pass-and-play screen
            // ALSO: If player changed (even if same human ID but new turn cycle), clear undo stack
            // to prevent undoing into previous hand/turn
            
            // Simplified: If the current player ID is different from the player in the last snapshot,
            // OR if the hand size reset (meaning a new hand started), logic is tricky.
            // Safest: Clear undo stack on turn switch to keep "current hand only" rule.
            
            // Wait - handleTurnStart is called AFTER 'applyLocalMove'.
            // If we just placed the 1st pin, handleTurnStart is called for the SAME player.
            // We want to KEEP the stack then.
            // Only clear if the Player ID actually Changed from the previous turn/hand owner.
            
            // Ideally: If specific new player is different from 'previousHuman' or it's a new hand set?
            // Actually, 'applyLocalMove' does NOT change playerId if the hand continues.
            // So checking 'if (previousHuman != currentPlayerId)' detects actual turn switches.
            if (previousHuman != currentPlayerId) {
                undoStack.clear()
            }
            
            // If different human than before, show pass-and-play screen
            if (previousHuman != null && previousHuman != currentPlayerId) {
                showPassPlayScreen(state)
            } else {
                // Same human or first turn - go directly to game
                if (NavigationManager.getCurrentScreen() != Screen.LOCAL_GAME) {
                    NavigationManager.navigateTo(Screen.LOCAL_GAME)
                    window.setTimeout({ setupBoardClickHandler() }, 100)
                }
                renderAll(state)
            }
        }
    }

    
    /**
     * Show pass-and-play screen between human turns.
     */
    private fun showPassPlayScreen(state: GameState) {
        val currentPlayer = state.players.find { it.id == state.currentPlayerId }
        val playerName = currentPlayer?.name ?: "Next Player"
        
        // Update pass-play screen content
        document.getElementById("pass-play-title")?.textContent = "Pass the Device"
        document.getElementById("pass-play-message")?.textContent = "It's $playerName's turn"
        document.getElementById("pass-play-player")?.textContent = playerName
        
        NavigationManager.navigateTo(Screen.PASS_PLAY)
    }
    
    /**
     * Confirm pass-and-play - player is ready.
     */
    fun confirmPassPlay() {
        val state = gameEngine?.getCurrentGameState() ?: return
        NavigationManager.navigateTo(Screen.LOCAL_GAME)
        window.setTimeout({ setupBoardClickHandler() }, 100)
        renderAll(state)
    }
    
    /**
     * Schedule AI move with delay.
     */
    private fun scheduleAIMove(ai: AIPlayer, state: GameState) {
        // Don't schedule if game is paused
        if (isGamePaused) {
            console.log("LocalGameManager: Game paused - deferring AI move")
            return
        }
        
        // Use user-defined AI speed, or fall back to difficulty-based defaults if not set (or set to 0)
        val userSpeed = SettingsManager.getSettings().aiSpeed
        val delay = if (userSpeed > 0) {
            userSpeed
        } else {
            when (ai.difficulty?.uppercase()) {
                "EASY" -> 500
                "MEDIUM" -> 800
                "HARD" -> 1000
                "EXPERT" -> 1200
                else -> 500
            }
        }
        
        // Update turn indicator
        val indicator = document.getElementById("local-turn-indicator")
        indicator?.textContent = "🤔 ${ai.name} is thinking..."
        indicator?.classList?.add("thinking")
        
        pendingAITimeoutId = window.setTimeout({
            pendingAITimeoutId = null
            makeAIMove(ai)
        }, delay)
    }
    
    /**
     * Pause the game (for ads).
     */
    private fun pauseGame() {
        console.log("LocalGameManager: ⏸️ Pausing game")
        isGamePaused = true
        
        // Cancel any pending AI move
        pendingAITimeoutId?.let {
            window.clearTimeout(it)
            pendingAITimeoutId = null
            console.log("LocalGameManager: Cancelled pending AI move")
        }
    }
    
    /**
     * Resume the game (after ad).
     */
    private fun resumeGame() {
        console.log("LocalGameManager: ▶️ Resuming game")
        isGamePaused = false
        
        // Check if it's AI's turn and reschedule
        val state = gameEngine?.getCurrentGameState() ?: return
        val currentPlayerId = state.currentPlayerId ?: return
        val aiOpponent = aiOpponents[currentPlayerId]
        
        if (aiOpponent != null) {
            console.log("LocalGameManager: Rescheduling AI move for ${aiOpponent.name}")
            scheduleAIMove(aiOpponent, state)
        }
    }
    
    /**
     * Make AI move.
     */
    private fun makeAIMove(ai: AIPlayer) {
        if (isGameOver) return
        
        val engine = gameEngine ?: return
        val state = engine.getCurrentGameState() ?: return
        
        // Verify it's still this AI's turn
        if (state.currentPlayerId != ai.id) {
            console.log("LocalGameManager: Not ${ai.name}'s turn anymore")
            return
        }
        
        val hand = state.currentHand
        if (hand.isEmpty()) {
            console.log("LocalGameManager: AI has no pins")
            return
        }
        
        // Create BoardAnalyzer for AI strategy
        val boardAnalyzer = BoardAnalyzer()
        
        val move = ai.generateMove(state, hand, boardAnalyzer) // Correct signature: (gameState, pins, boardAnalyzer)
        if (move != null) {
            console.log("LocalGameManager: ${ai.name} placing ${move.pin.color} at ${move.position}")
            try {
                // Calculate score before applying move
                val scoreResult = scoreCalculator.calculateMoveScore(state.board, move)
                updateScoreDisplay(scoreResult.points, ai.id)
                
                // Play placement and score sounds
                SoundManager.playPinPlaced()
                SoundManager.playScoreSound(scoreResult.points)
                if (scoreResult.pinsToReverse.isNotEmpty()) {
                    SoundManager.playPinsReversed()
                }
                
                // Trigger flash using new scoringPositions field
                triggerScoreFlash(scoreResult.scoringPositions)
                
                engine.applyLocalMove(move)
                
                // Auto-save game state after AI move completes
                autoSaveGameState()
            } catch (e: Exception) {
                console.error("LocalGameManager: AI move failed: ${e.message}")
            }
        }
    }
    
    /**
     * Auto-save game state to PlayGama storage after each move.
     * Only saves if PlayGama storage is available.
     */
    private fun autoSaveGameState() {
        if (!PlayGamaStorageManager.isSaveRestoreAvailable()) {
            return
        }
        
        try {
            val state = gameEngine?.getCurrentGameState() ?: return
            
            // Capture all pins on the board
            val allPins = mutableListOf<SavedPin>()
            for (sectionRow in 0..2) {
                for (sectionCol in 0..2) {
                    val section = state.board.getSection(sectionRow, sectionCol)
                    val occupiedPositions = section.getOccupiedPositions()
                    for ((localRow, localCol) in occupiedPositions) {
                        val pos = Position(sectionRow, sectionCol, localRow, localCol)
                        val pin = state.board.getPinAt(pos) ?: continue
                        allPins.add(SavedPin(
                            sectionRow = pos.sectionRow,
                            sectionCol = pos.sectionCol,
                            localRow = pos.localRow,
                            localCol = pos.localCol,
                            color = pin.color.name,
                            isJoker = pin.isJoker,
                            isReversed = pin.isReversed,
                            isJokerTurnedBlack = pin.isJokerTurnedBlack
                        ))
                    }
                }
            }
            
            // Build save state JSON
            val saveState = GameSaveState(
                version = 1,
                gameMode = "LOCAL_MULTIPLAYER",
                boardSeed = state.board.boardSeed ?: 0L,
                dressColors = getDressColorsFromSeed(state.board.boardSeed),
                boardPins = allPins,
                players = state.players.map { player ->
                    SavedPlayer(
                        id = player.id,
                        name = player.name,
                        score = player.score,
                        jokersUsed = player.jokersUsed,
                        isAI = player.isAI
                    )
                },
                currentPlayerIndex = state.currentPlayerIndex,
                currentHandNumber = state.handCount,
                lastSaveTimestamp = kotlin.js.Date.now().toLong(),
                currentHand = state.currentHand.map { it.color.name }
            )
            
            val jsonString = saveState.toJson()
            
            PlayGamaStorageManager.saveGameState(jsonString) { success ->
                if (success) {
                    console.log("💾 Local game auto-saved to PlayGama storage")
                } else {
                    console.warn("⚠️ Failed to save local game to PlayGama storage")
                }
            }
        } catch (e: Exception) {
            console.error("❌ Failed to auto-save local game: ${e.message}")
        }
    }
    
    // Helper: dressColors might not be on GameState directly, get from board
    private fun getDressColorsFromSeed(seed: Long?): List<String> {
        if (seed == null) {
            // Return default colors if no seed
            return listOf("RED", "GREEN", "PURPLE", "YELLOW")
        }
        // Generate colors from seed using same logic as Board
        val colorList = listOf(PinColor.RED, PinColor.GREEN, PinColor.PURPLE, PinColor.YELLOW)
        // Use JS Math.random() for simplicity - board uses SeededRandom but we just need consistent colors
        val random = kotlin.js.js("Math.random()")
        val shuffled = colorList.shuffled()
        return shuffled.take(4).map { it.name }
    }
    
    /**
     * Handle board click for human player.
     */
    fun handleBoardClick(row: Int, col: Int) {
        // Multi-Path Synchronous Jolt (Phase 5 Nuclear Fix)
        SoundManager.joltAudio()
        
        val engine = gameEngine ?: return
        val state = engine.getCurrentGameState() ?: return
        
        // Check if it's a human's turn
        val currentPlayerId = state.currentPlayerId ?: return
        if (aiOpponents.containsKey(currentPlayerId)) {
            console.log("LocalGameManager: Not a human's turn")
            return
        }
        
        if (isGameOver) return
        
        val position = Position.fromLegacy(row, col)
        if (!state.board.isEmpty(position)) {
            console.log("LocalGameManager: Position occupied")
            return
        }
        
        // Check for pending joker
        val jokerPin = pendingJokerPin
        if (jokerPin != null) {
            try {
                val move = Move(currentPlayerId, jokerPin, position)
                
                // Save state for undo BEFORE applying
                undoStack.add(UndoSnapshot(state, playerHandScores.toMap(), previousHandSize))
                
                // Calculate score before applying
                val scoreResult = scoreCalculator.calculateMoveScore(state.board, move)
                updateScoreDisplay(scoreResult.points, currentPlayerId)
                
                // Play placement and score sounds
                SoundManager.playPinPlaced()
                SoundManager.playScoreSound(scoreResult.points)
                if (scoreResult.pinsToReverse.isNotEmpty()) {
                    SoundManager.playPinsReversed()
                }
                
                // Trigger flash using new scoringPositions field
                triggerScoreFlash(scoreResult.scoringPositions)
                
                engine.applyLocalMove(move)
                pendingJokerPin = null
                selectedPinIndex = 0
            } catch (e: Exception) {
                console.error("LocalGameManager: Joker move failed: ${e.message}")
                // Remove failed snapshot
                if (undoStack.isNotEmpty()) undoStack.removeAt(undoStack.lastIndex)
            }
            return
        }
        
        // Regular pin placement
        val hand = state.currentHand
        if (selectedPinIndex >= hand.size) {
            console.log("LocalGameManager: Invalid pin selection")
            return
        }
        
        val pin = hand[selectedPinIndex]
        try {
            val move = Move(currentPlayerId, pin, position)
            console.log("LocalGameManager: Human player making move - pin=${pin.color}, position=$position, playerId=$currentPlayerId")
            
            // Save state for undo BEFORE applying
            undoStack.add(UndoSnapshot(state, playerHandScores.toMap(), previousHandSize))
            
            // Calculate score before applying
            val scoreResult = scoreCalculator.calculateMoveScore(state.board, move)
            console.log("LocalGameManager: Score calculated for ${pin.color} at $position: ${scoreResult.points} points")
            console.log("LocalGameManager: About to call updateScoreDisplay with points=${scoreResult.points}, playerId=$currentPlayerId")
            updateScoreDisplay(scoreResult.points, currentPlayerId)
            console.log("LocalGameManager: updateScoreDisplay completed")
            
            // Play placement and score sounds
            SoundManager.playPinPlaced()
            SoundManager.playScoreSound(scoreResult.points)
            if (scoreResult.pinsToReverse.isNotEmpty()) {
                SoundManager.playPinsReversed()
            }
            
            // Trigger flash using new scoringPositions field
            triggerScoreFlash(scoreResult.scoringPositions)
            
            console.log("LocalGameManager: About to call engine.applyLocalMove")
            engine.applyLocalMove(move)
            console.log("LocalGameManager: engine.applyLocalMove completed")
            selectedPinIndex = 0
            
            // Auto-save game state after successful move
            autoSaveGameState()
        } catch (e: Exception) {
            console.error("LocalGameManager: Move failed: ${e.message}")
            if (undoStack.isNotEmpty()) undoStack.removeAt(undoStack.lastIndex)
        }
    }

    
    /**
     * Select a pin from hand.
     */
    fun selectHandPin(index: Int) {
        // Proactively unlock audio on pin selection (valid user gesture)
        SoundManager.unlockAudio()
        
        val state = gameEngine?.getCurrentGameState() ?: return
        if (index < state.currentHand.size) {
            selectedPinIndex = index
            renderHand(state)
        }
    }
    
    /**
     * Start joker creation.
     */
    fun sacrificePinForJoker(index: Int) {
        // Proactively unlock audio on joker sacrifice (valid user gesture)
        SoundManager.unlockAudio()
        
        val state = gameEngine?.getCurrentGameState() ?: return
        val currentPlayerId = state.currentPlayerId ?: return
        
        // Only humans can create jokers via UI
        if (aiOpponents.containsKey(currentPlayerId)) return
        if (isGameOver) return
        
        // Check if player can still use jokers
        val player = state.players.find { it.id == currentPlayerId }
        if (player?.canUseJoker() != true) {
            window.alert("You have already used the maximum number of jokers (4) this game!")
            return
        }
        
        if (index >= state.currentHand.size) return
        
        val pin = state.currentHand[index]
        if (pin.isJoker) return
        
        pendingJokerIndex = index
        showJokerDialogForHand()
    }
    
    /**
     * Select joker color.
     */
    fun selectJokerColor(colorName: String) {
        // Proactively unlock audio on joker color selection (valid user gesture)
        SoundManager.unlockAudio()
        
        val index = pendingJokerIndex ?: return
        val state = gameEngine?.getCurrentGameState() ?: return
        if (index >= state.currentHand.size) return
        
        val originalPin = state.currentHand[index]
        val jokerColor = try {
            PinColor.valueOf(colorName.uppercase())
        } catch (e: Exception) {
            console.error("Invalid joker color: $colorName")
            return
        }
        
        pendingJokerPin = Pin.joker(originalColor = originalPin.color, playedAsColor = jokerColor)
        pendingJokerIndex = null
        selectedPinIndex = index
        
        hideJokerDialog()
        
        val newState = gameEngine?.getCurrentGameState() ?: return
        renderHand(newState)
    }
    
    /**
     * Cancel joker creation.
     */
    fun cancelJoker() {
        pendingJokerIndex = null
        pendingJokerPin = null
        hideJokerDialog()
        val state = gameEngine?.getCurrentGameState() ?: return
        renderHand(state)
    }

    private fun hideJokerDialog() {
        val dialog = document.getElementById("joker-dialog") as? HTMLElement ?: return
        dialog.style.display = "none"
    }
    
    /**
     * Request undo.
     */
    fun requestUndo() {
        // Proactively unlock audio on undo request (valid user gesture)
        SoundManager.unlockAudio()
        
        if (undoStack.isEmpty() || !RewardedAdManager.hasUndoCredits()) return
        
        // Interrupt any pending turn switch (undoing the 4th pin)
        if (turnTransitionTimeoutId != null) {
            console.log("LocalGameManager: Interrupting turn transition for Undo")
            window.clearTimeout(turnTransitionTimeoutId!!)
            turnTransitionTimeoutId = null
        }
        
        val engine = gameEngine ?: return
        
        // Pop snapshot
        val snapshot = undoStack.removeAt(undoStack.lastIndex)
        
        // Restore State
        engine.restoreState(snapshot.state)
        
        // Restore UI vars
        playerHandScores.clear()
        playerHandScores.putAll(snapshot.handScores)
        previousHandSize = snapshot.prevHandSize
        
        RewardedAdManager.useUndoCredit()
        saveUndoCredits()
        
        // Reset legacy trackers (just in case)
        lastPinScore = null
        selectedPinIndex = 0
        
        // Cancel flash
        scoreFlashIntervalId?.let { window.clearInterval(it) }
        scoreFlashTimeoutId?.let { window.clearTimeout(it) }
        isFlashVisible = false
        flashingPositions = emptySet()
        
        console.log("LocalGameManager: Undo successful, stack size: ${undoStack.size}")
        
        // Render restored state
        val newState = engine.getCurrentGameState() ?: return
        renderAll(newState)
        updateUndoUI()
    }
    
    /**
     * Watch ad to earn undo credit.
     */
    fun watchAdForCredit() {
        // Proactively unlock audio on ad request (valid user gesture)
        SoundManager.unlockAudio()
        
        RewardedAdManager.showRewardedAd(
            onRewardEarned = {
                console.log("LocalGameManager: Ad completed, adding undo credit")
                // addUndoCredits is now called INSIDE showRewardedAd
                updateUndoUI()
            },
            onAdClosed = {
                console.log("LocalGameManager: Ad closed")
            },
            onAdFailed = {
                console.log("LocalGameManager: Ad failed")
            }
        )
    }
    
    /**
     * Handle game over.
     */
    private fun handleGameOver(state: GameState) {
        console.log("LocalGameManager: handleGameOver called - showing interstitial ad")
        isGameOver = true
        undoStack.clear()
        PlayGamaStorageManager.clearSavedGame()
        
        // Show interstitial ad at game end - callbacks will pause/resume
        co.uk.paxes.pinpoint.web.BannerAdManager.showInterstitialAd("interstitial_game_over",
            onAdShowing = {
                console.log("⏸️ LocalGameManager: Game-over ad is showing - pausing game")
                isGamePaused = true
            },
            onClose = {
                console.log("✅ LocalGameManager: Game-over ad closed - resuming game")
                isGamePaused = false
                displayGameOverScreen(state)
            }
        )
    }
    
    /**
     * Display the game over screen with scores.
     */
    private fun displayGameOverScreen(state: GameState) {
        // Find winner
        val sortedPlayers = state.players.sortedByDescending { it.score }
        val winner = sortedPlayers.first()
        val isTie = sortedPlayers.size > 1 && sortedPlayers[0].score == sortedPlayers[1].score
        
        // Show the Game Over dialog
        val dialog = document.getElementById("game-over-dialog")
        val winnerDisplay = document.getElementById("game-over-winner")
        val scoresList = document.getElementById("game-over-scores")
        
        if (winnerDisplay != null) {
            val winnerHtml = StringBuilder()
            if (!isTie) {
                winnerHtml.append("<span class=\"winner-label\">Winner</span>")
                winnerHtml.append("<span class=\"winner-name\">${winner.name} Wins!</span>")
            } else {
                winnerHtml.append("<span class=\"winner-name\">TIE GAME</span>")
            }
            winnerDisplay.innerHTML = winnerHtml.toString()
        }
        
        if (scoresList != null) {
            val scoresHtml = StringBuilder()
            sortedPlayers.forEach { player ->
                // In local game, "me" doesn't strictly apply, or it's the current player
                val isCurrent = player.id == state.currentPlayerId
                scoresHtml.append("<div class=\"final-score-row ${if (isCurrent) "is-me" else ""}\">")
                scoresHtml.append("<span class=\"player-name\">${player.name}</span>")
                scoresHtml.append("<span class=\"player-score\">${player.score}</span>")
                scoresHtml.append("</div>")
            }
            scoresList.innerHTML = scoresHtml.toString()
        }
        
        dialog?.asDynamic()?.style?.display = "flex"
        
        val indicator = document.getElementById("local-turn-indicator")
        indicator?.classList?.remove("thinking", "my-turn", "ai-turn")
        
        if (isTie) {
            indicator?.innerHTML = "🤝 It's a Tie! ${winner.score} points"
        } else {
            indicator?.innerHTML = "🎉 ${winner.name} Wins! ${winner.score} points"
            indicator?.classList?.add("my-turn")
        }
        
        renderBoard(state)
        renderPlayerScoresPanel(state)
    }
    
    /**
     * Quit game.
     */
    fun quitGame() {
        gameEngine = null
        aiOpponents = mutableMapOf()
        isGameOver = true
        NavigationManager.navigateTo(Screen.MAIN_MENU)
    }

    
    // ========== Rendering ==========
    
    private fun renderAll(state: GameState) {
        renderBoard(state)
        renderPlayerScoresPanel(state)
        renderHand(state)
        updateTurnIndicator(state)
        updateUndoUI()
    }
    
    /**
     * Update score tracking after a move.
     */
    private fun updateScoreDisplay(points: Int, playerId: String) {
        console.log("LocalGameManager: updateScoreDisplay called - points=$points, playerId=$playerId")
        lastPinScore = points
        lastPinScorePlayerId = playerId
        
        // Update hand score for this player
        val currentHandScore = playerHandScores[playerId] ?: 0
        playerHandScores[playerId] = currentHandScore + points
        console.log("LocalGameManager: Updated scores - lastPinScore=$lastPinScore, handScore=${playerHandScores[playerId]}")
        
        // Force immediate UI update to show the pin score
        val state = gameEngine?.getCurrentGameState()
        if (state != null) {
            renderPlayerScoresPanel(state)
            console.log("LocalGameManager: Forced UI update after score change")
        }
    }
    
    /**
     * Trigger the score flash animation for the given positions.
     * High-contrast white flashing effect matching Android.
     */
    private fun triggerScoreFlash(positions: List<Position>) {
        if (positions.isEmpty()) return
        
        console.log("⚡ [Local] Triggering score flash for ${positions.size} pins")
        
        // Cancel any existing flash
        scoreFlashIntervalId?.let { window.clearInterval(it) }
        scoreFlashTimeoutId?.let { window.clearTimeout(it) }
        
        flashingPositions = positions.toSet()
        isFlashVisible = true
        gameEngine?.getCurrentGameState()?.let { renderBoard(it) }
        
        // Toggle flash every 400ms for 2 seconds
        scoreFlashIntervalId = window.setInterval({
            isFlashVisible = !isFlashVisible
            gameEngine?.getCurrentGameState()?.let { renderBoard(it) }
        }, 400)
        
        // End flash after 2 seconds
        scoreFlashTimeoutId = window.setTimeout({
            scoreFlashIntervalId?.let { window.clearInterval(it) }
            scoreFlashIntervalId = null
            scoreFlashTimeoutId = null
            flashingPositions = emptySet()
            isFlashVisible = false
            gameEngine?.getCurrentGameState()?.let { renderBoard(it) }
            console.log("⚡ [Local] Score flash finished")
        }, 2000)
    }
    
    
    private fun setupBoardClickHandler() {
        val canvas = document.getElementById("local-game-board") as? HTMLCanvasElement ?: return
        canvas.onclick = { event ->
            // Proactively unlock audio on board click (valid user gesture)
            SoundManager.unlockAudio()
            
            val mouseEvent = event as MouseEvent
            val rect = canvas.getBoundingClientRect()
            
            // Use the displayed size for calculations (like Main.kt does)
            // This works correctly when CSS scales the canvas
            val displayedWidth = rect.width
            val displayedHeight = rect.height
            val cellSize = displayedWidth / 10.0
            
            val x = mouseEvent.clientX - rect.left
            val y = mouseEvent.clientY - rect.top
            val col = (x / cellSize).toInt().coerceIn(0, 9)
            val row = (y / cellSize).toInt().coerceIn(0, 9)
            
            console.log("LocalGameManager: Click at ($x, $y), displayed size: ${displayedWidth}x${displayedHeight}, cell: ($row, $col)")
            
            handleBoardClick(row, col)
        }
    }
    
    private fun renderBoard(state: GameState) {
        val canvas = document.getElementById("local-game-board") as? HTMLCanvasElement
        if (canvas == null) {
            console.error("FATAL: local-game-board canvas not found")
            return
        }

        try {
            val ctx = canvas.getContext("2d") as CanvasRenderingContext2D
            val rect = canvas.getBoundingClientRect()
            
            // Fix for canvas size vs display size issues
            // Ensure internal resolution matches display size for sharpness
            // This also prevents "blue screen" if width/height are 0
            if (canvas.width != 400 || canvas.height != 400) {
                 canvas.width = 400
                 canvas.height = 400
            }
            
            val displayedWidth = if (rect.width > 0) rect.width else 400.0
            
            // Use fixed internal size for drawing
            val width = canvas.width.toDouble()
            val height = canvas.height.toDouble()
            val cellSize = width / 10.0
            
            // Background
            ctx.fillStyle = "#2E2E2E"
            ctx.fillRect(0.0, 0.0, width, height)
            
            // Grid lines
            ctx.strokeStyle = "#00FFFF"
            ctx.lineWidth = 3.0
            for (gridLine in listOf(3, 6)) {
                val pos = gridLine * cellSize + cellSize / 2
                ctx.beginPath()
                ctx.moveTo(pos, 0.0)
                ctx.lineTo(pos, height)
                ctx.stroke()
                ctx.beginPath()
                ctx.moveTo(0.0, pos)
                ctx.lineTo(width, pos)
                ctx.stroke()
            }
            
            // Cells
            for (row in 0 until 10) {
                for (col in 0 until 10) {
                    val position = Position.fromLegacy(row, col)
                    val pin = state.board.getPinAt(position)
                    drawCell(ctx, row, col, pin, cellSize)
                }
            }
            
            // Cursor
            val isHumanTurn = state.currentPlayerId?.let { !aiOpponents.containsKey(it) } ?: false
            canvas.style.cursor = if (isHumanTurn && !isGameOver) "pointer" else "default"
        } catch (e: Exception) {
            console.error("FATAL: renderBoard crashed: ${e.message}")
            // Don't alert here to avoid spam loop, but log clearly
        }
    }
    
    private fun drawCell(ctx: CanvasRenderingContext2D, row: Int, col: Int, pin: Pin?, cellSize: Double) {
        val x = col * cellSize + cellSize / 2
        val y = row * cellSize + cellSize / 2
        val holeRadius = cellSize * 0.42
        val pinRadius = cellSize * 0.32
        
        // Hole
        ctx.beginPath()
        ctx.arc(x, y, holeRadius, 0.0, 2 * kotlin.math.PI)
        ctx.fillStyle = "#757575"
        ctx.fill()
        ctx.strokeStyle = "#424242"
        ctx.lineWidth = 0.5
        ctx.stroke()
        
        // Pin
        if (pin != null) {
            val effectiveColor = pin.getEffectiveColor()
            val fillColor = PIN_COLORS[effectiveColor] ?: "#666"
            
            ctx.beginPath()
            ctx.arc(x, y, pinRadius, 0.0, 2 * kotlin.math.PI)
            ctx.fillStyle = fillColor
            ctx.fill()
            ctx.strokeStyle = "rgba(0, 0, 0, 0.3)"
            ctx.lineWidth = 0.5
            ctx.stroke()
            
            if (pin.isJoker) {
                ctx.fillStyle = "#fff"
                ctx.font = "bold ${(cellSize * 0.35).toInt()}px Arial"
                ctx.textAlign = CanvasTextAlign.CENTER
                ctx.textBaseline = CanvasTextBaseline.MIDDLE
                ctx.fillText("J", x, y)
            }
        }
        
        // Draw flash highlight if active for this position
        if (flashingPositions.contains(Position.fromLegacy(row, col)) && isFlashVisible) {
            // Draw flash around the PIN (not the hole) for better visibility
            val flashRadius = if (pin != null) pinRadius * 1.2 else holeRadius * 1.1
            
            ctx.beginPath()
            ctx.arc(x, y, flashRadius, 0.0, 2 * kotlin.math.PI)
            ctx.strokeStyle = "#FFFFFF"
            ctx.lineWidth = 4.0
            ctx.stroke()
            
            // Add a subtle outer glow
            ctx.beginPath()
            ctx.arc(x, y, flashRadius * 1.15, 0.0, 2 * kotlin.math.PI)
            ctx.strokeStyle = "rgba(255, 255, 255, 0.5)"
            ctx.lineWidth = 2.0
            ctx.stroke()
        }
    }
    

    private fun renderPlayerScoresPanel(state: GameState) {
        val panel = document.getElementById("local-player-scores-panel") ?: return
        
        val html = StringBuilder()
        
        // Pin and Hand score display - show based on settings
        val settings = SettingsManager.getSettings()
        val showPinScore = settings.showCurrentPinScore
        val showHandScore = settings.showCurrentHandScore
        
        if (showPinScore || showHandScore) {
            html.append("<div class=\"current-scores\">")
            if (showPinScore) {
                val pinScore = lastPinScore ?: 0
                val pinDisplay = if (pinScore > 0) "+$pinScore" else "0"
                console.log("LocalGameManager: Rendering pin score - lastPinScore=$lastPinScore, display=$pinDisplay, playerId=$lastPinScorePlayerId")
                html.append("<span class=\"pin-score\">Pin: $pinDisplay</span>")
            }
            if (showHandScore) {
                val playerId = lastPinScorePlayerId ?: state.currentPlayerId
                val handScore = playerId?.let { playerHandScores[it] } ?: 0
                val handDisplay = handScore.toString()
                console.log("LocalGameManager: Rendering hand score - playerId=$playerId, handScore=$handScore")
                html.append("<span class=\"hand-score\">Hand: $handDisplay</span>")
            }
            html.append("</div>")
        }
        
        // Add class for 3 players to handle grid layout
        val playerCount = state.players.size
        val gridClass = if (playerCount == 3) "player-scores-grid three-players" else "player-scores-grid"
        html.append("<div class=\"$gridClass\">")
        
        for (player in state.players) {
            val isCurrentTurn = player.id == state.currentPlayerId
            val isAI = aiOpponents.containsKey(player.id)
            
            val cardClass = buildString {
                append("player-score-card")
                if (isAI) append(" ai-player")
                if (isCurrentTurn) append(" active-turn")
            }
            
            html.append("<div class=\"$cardClass\">")
            if (isCurrentTurn) {
                html.append("<span class=\"turn-arrow\">▶</span>")
            }
            val icon = if (isAI) "${getDifficultyEmoji(player.id)} " else ""
            html.append("<span class=\"player-name\">$icon${player.name}</span>")
            html.append("<span class=\"player-score\">${player.score}</span>")
            
            // Add joker count to player card
            val jokersRemaining = 4 - player.jokersUsed
            html.append("<div class=\"player-jokers\">")
            for (i in 0 until 4) {
                val status = if (i < jokersRemaining) "available" else "used"
                html.append("<span class=\"joker-dot $status\"></span>")
            }
            html.append("</div>")
            
            html.append("</div>")
        }
        
        html.append("</div>")
        panel.innerHTML = html.toString()
    }

    
    private fun renderHand(state: GameState) {
        val container = document.getElementById("local-hand-pins") ?: return
        val handContainer = document.getElementById("local-player-hand") ?: return
        val handTitle = document.getElementById("local-hand-title")
        
        val currentPlayerId = state.currentPlayerId
        val isAITurn = currentPlayerId?.let { aiOpponents.containsKey(it) } ?: false
        val currentPlayer = state.players.find { it.id == currentPlayerId }
        
        // Update hand title
        handTitle?.textContent = if (isAITurn) {
            "${currentPlayer?.name ?: "AI"}'s Turn"
        } else {
            "${currentPlayer?.name ?: "Your"}'s Hand".replace("Your's", "Your")
        }

        // Joker status text removed - now visually shown as dots on player score cards
        
        if (isGameOver) {
            handContainer.classList.add("not-your-turn")
            container.innerHTML = "<div class=\"loading\">Game Over</div>"
            return
        }
        
        // Set appropriate CSS class for AI turns
        if (isAITurn) {
            handContainer.classList.add("not-your-turn")
        } else {
            handContainer.classList.remove("not-your-turn")
        }
        
        val hand = state.currentHand
        if (hand.isEmpty()) {
            container.innerHTML = "<div class=\"loading\">Waiting...</div>"
            return
        }
        
        // Use pending joker if one is being created (only for human players)
        val displayHand = if (!isAITurn && pendingJokerIndex != null && pendingJokerPin != null) {
            hand.toMutableList().also {
                it[pendingJokerIndex!!] = pendingJokerPin!!
            }
        } else {
            hand
        }
        
        val html = StringBuilder()
        displayHand.forEachIndexed { index, pin ->
            val colorClass = if (pin.isJoker) "joker" else pin.color.name.lowercase()
            val selectedClass = if (!isAITurn && index == selectedPinIndex) " selected" else ""
            val label = if (pin.isJoker) "J" else ""
            
            html.append("<div class=\"hand-pin-container\">")
            
            // For AI turns, don't add click handlers
            if (isAITurn) {
                html.append("<div class=\"hand-pin $colorClass$selectedClass\">")
            } else {
                html.append("<div class=\"hand-pin $colorClass$selectedClass\" onclick=\"selectLocalHandPin($index)\">")
            }
            html.append(label)
            html.append("</div>")
            
            // Joker button - only show for human players
            if (!isAITurn) {
                val player = state.players.find { it.id == currentPlayerId }
                if (!pin.isJoker && player?.canUseJoker() == true) {
                    html.append("<button class=\"joker-btn\" onclick=\"localSacrificePinForJoker($index)\">JOKER</button>")
                }
            }
            html.append("</div>")
        }
        container.innerHTML = html.toString()
    }
    
    // OLD INTERFACE: Removed inline joker selection rendering
    
    private fun showJokerDialogForHand() {
        val dialog = document.getElementById("joker-dialog") as? HTMLElement ?: return
        dialog.style.display = "flex"
        
        // Set up color button click handlers for LOCAL game
        val buttons = document.querySelectorAll(".joker-color-btn")
        for (i in 0 until buttons.length) {
            val btn = buttons.item(i) as? HTMLElement ?: continue
            val color = btn.getAttribute("data-color") ?: continue
            btn.onclick = { selectJokerColor(color) }
        }
        
        // Update cancel button to call localCancelJoker
        val cancelBtn = dialog.querySelector(".btn-secondary") as? HTMLElement
        cancelBtn?.onclick = { cancelJoker() }
    }
    
    private fun updateTurnIndicator(state: GameState) {
        val indicator = document.getElementById("local-turn-indicator") ?: return
        indicator.classList.remove("my-turn", "ai-turn", "thinking")
        
        if (isGameOver) return
        
        val currentPlayerId = state.currentPlayerId
        val currentPlayer = state.players.find { it.id == currentPlayerId }
        val isAI = currentPlayerId?.let { aiOpponents.containsKey(it) } ?: false
        
        if (isAI) {
            indicator.textContent = "${currentPlayer?.name}'s turn"
            indicator.classList.add("ai-turn")
        } else {
            indicator.textContent = "${currentPlayer?.name}'s turn - tap to place a pin"
            indicator.classList.add("my-turn")
        }
    }
    
    private fun updateUndoUI() {
        val creditsDisplay = document.getElementById("local-undo-credits-display")
        val currentCredits = RewardedAdManager.getUndoCredits()
        creditsDisplay?.textContent = currentCredits.toString()
        
        val undoBtn = document.getElementById("local-btn-undo") as? HTMLButtonElement
        val hasUndos = undoStack.isNotEmpty()
        undoBtn?.disabled = !hasUndos || currentCredits <= 0
        
        val undoStatus = document.getElementById("local-undo-status")
        val activePlayer = gameEngine?.getCurrentGameState()?.players?.find { it.id == gameEngine?.getCurrentGameState()?.currentPlayerId }
        val isAIActive = activePlayer?.isAI == true
        
        if (!isAIActive && !isGameOver) {
            undoStatus?.asDynamic()?.style?.display = "flex"
            if (hasUndos && currentCredits > 0) {
                undoBtn?.classList?.add("active")
            } else {
                undoBtn?.classList?.remove("active")
            }
        } else {
            undoStatus?.asDynamic()?.style?.display = "none"
        }
    }
    
    private fun loadUndoCredits() {
        // Now handled by RewardedAdManager
    }
    
    private fun saveUndoCredits() {
        // Now handled by RewardedAdManager
    }
    
    /**
     * Restore a saved game state.
     * Called from Main.kt when user chooses to restore a saved game.
     */
    fun restoreGame(savedState: GameSaveState) {
        console.log("LocalGameManager: Starting restoreGame with ${savedState.players.size} players")
        
        // Set flag to skip premature game-over checks during startup
        isRestoringGame = true
        
        // Clear undo stack
        undoStack.clear()
        
        // Reset hand scores
        playerHandScores.clear()
        
        // Reconstruct player configurations for the new game
        playerConfigs = savedState.players.map { savedPlayer ->
            PlayerConfig(
                type = if (savedPlayer.isAI) "ai" else "human",
                name = savedPlayer.name,
                difficulty = "easy"  // Default difficulty for restored games
            )
        }
        
        // Clear AI opponents map
        aiOpponents = mutableMapOf()
        
        // Create players list
        val players = mutableListOf<Player>()
        playerConfigs.forEachIndexed { index, config ->
            val playerId = "player-$index"
            val isAI = config.type == "ai"
            val player = Player(
                id = playerId,
                name = config.name,
                isAI = isAI
            )
            players.add(player)
            
            // Create AI opponent if needed
            if (isAI) {
                aiOpponents.put(playerId, AIPlayer(
                    id = playerId,
                    name = config.name,
                    difficulty = "easy"
                ))
            }
        }
        
        // Create game config with restored seed
        val config = GameConfig(
            players = players,
            startingPlayerId = players.getOrNull(savedState.currentPlayerIndex)?.id ?: players.first().id,
            boardSeed = savedState.boardSeed,
            isNetworkClient = false
        )
        
        // Create new game engine
        val engine = GameEngine(listener = object : GameEngineListener {
            override fun onGameStarted(state: GameState) {
                console.log("LocalGameManager: Restored game started")
                handleTurnStart(state)
            }
            
            override fun onMoveApplied(state: GameState, move: Move) {
                val occupiedCount = state.board.getOccupiedPositions().size
                console.log("LocalGameManager: onMoveApplied called - move by ${move.playerId}, isRestoringGame=$isRestoringGame, board occupied=${occupiedCount}/100")
                
                val isNewHand = state.currentHand.size == 4 && previousHandSize <= 1
                if (isNewHand) {
                    val newPlayerId = state.currentPlayerId
                    if (newPlayerId != null) {
                        console.log("LocalGameManager: New hand dealt for $newPlayerId in restored game")
                        playerHandScores[newPlayerId] = 0
                    }
                }
                
                previousHandSize = state.currentHand.size
                
                // ⚠️ CRITICAL: During restore, skip game-over check until pins are restored!
                // This check would trigger incorrectly during game startup before pins are placed.
                val boardIsFull = state.board.isFull()
                console.log("LocalGameManager: boardIsFull=$boardIsFull, isRestoringGame=$isRestoringGame, shouldCheckGameOver=${!isRestoringGame && boardIsFull}")
                
                if (!isRestoringGame && boardIsFull) {
                    console.log("LocalGameManager: Game over detected - board is full")
                    handleGameOver(state)
                } else {
                    if (isNewHand) {
                        turnTransitionTimeoutId = window.setTimeout({
                            turnTransitionTimeoutId = null
                            lastPinScore = null
                            lastPinScorePlayerId = null
                            handleTurnStart(state)
                        }, 3000)
                    } else {
                        handleTurnStart(state)
                    }
                }
            }
            
            override fun onGameEnded(state: GameState) {
                console.log("LocalGameManager: Restored game ended callback - board occupied=${state.board.getOccupiedPositions().size}/100, isRestoringGame=$isRestoringGame")
                if (!isRestoringGame) {
                    console.log("LocalGameManager: Not in restoring mode, calling handleGameOver")
                    handleGameOver(state)
                } else {
                    console.log("LocalGameManager: Still restoring, skipping handleGameOver")
                }
            }
        })
        
        gameEngine = engine
        
        // Start the game - this will deal the first hand
        try {
            engine.startGame(config)
            console.log("LocalGameManager: Game engine started with restored seed")
        } catch (e: Exception) {
            console.error("LocalGameManager: Failed to start restored game: ${e.message}")
            window.alert("Failed to restore game: ${e.message}")
            return
        }
        
        // Wait for game to start, then apply saved board state
        window.setTimeout({
            val gameState = engine.getCurrentGameState() ?: return@setTimeout
            
            // Create the restored board with saved pins (use empty board to avoid colliding with auto-generated dress pins)
            var restoredBoard = Board(initializeDressPins = false, randomSeed = savedState.boardSeed)
            console.log("LocalGameManager: Restoring ${savedState.boardPins.size} pins to board")
            
            for (savedPin in savedState.boardPins) {
                val pos = savedPin.toPosition()
                val pin = savedPin.toPin()
                
                // Place each saved pin on the board
                try {
                    restoredBoard = restoredBoard.placePinAt(pos, pin)
                } catch (e: Exception) {
                    console.error("LocalGameManager: Error placing pin at $pos: ${e.message}")
                }
            }
            
            // Restore the board state using the engine's method
            engine.restoreBoardState(restoredBoard)
            
            // Restore player scores and jokers - map by index since IDs are reassigned as player-0, player-1 etc.
            val restoredScoreMap = mutableMapOf<String, Int>()
            val restoredJokersMap = mutableMapOf<String, Int>()
            savedState.players.forEachIndexed { index, savedPlayer ->
                val playerId = "player-$index"
                restoredScoreMap[playerId] = savedPlayer.score
                restoredJokersMap[playerId] = savedPlayer.jokersUsed
                console.log("LocalGameManager: Restored score for ${savedPlayer.name} ($playerId): ${savedPlayer.score}, Jokers used: ${savedPlayer.jokersUsed}")
            }
            engine.updatePlayerScores(restoredScoreMap)
            engine.updatePlayerJokers(restoredJokersMap)
            
            // Restore playerHandScores to match saved scores
            playerHandScores.clear()
            playerHandScores.putAll(restoredScoreMap)
            
            // Restore the current hand if it was saved (to preserve mid-hand state)
            if (savedState.currentHand.isNotEmpty()) {
                val restoredHand = savedState.currentHand.mapNotNull { colorName ->
                    try { Pin(PinColor.valueOf(colorName)) } catch (e: Exception) { null }
                }
                if (restoredHand.isNotEmpty()) {
                    engine.updateCurrentHand(restoredHand)
                    console.log("LocalGameManager: Restored hand with ${restoredHand.size} pins: ${restoredHand.map { it.color }}")
                }
            }
            
            // Get the fully updated state (after board + scores + hand restoration)
            val finalState = engine.getCurrentGameState() ?: return@setTimeout
            
            console.log("LocalGameManager: Current player index restored to ${savedState.currentPlayerIndex}")
            console.log("LocalGameManager: Hand count restored to ${savedState.currentHandNumber}")
            console.log("LocalGameManager: Board has ${savedState.boardPins.size} pins after restoration")
            
            // NOW check if the restored game is already finished
            if (finalState.board.isFull()) {
                console.log("LocalGameManager: Restored board is full - game is finished")
                handleGameOver(finalState)
            } else {
                // Render the restored state
                renderAll(finalState)
                console.log("LocalGameManager: Game restored successfully")
            }
            
            // Clear the restore flag NOW - delay slightly to ensure any pending callbacks use the correct state
            window.setTimeout({
                isRestoringGame = false
                console.log("LocalGameManager: Restore flag cleared, game is now live")
            }, 500)
        }, 100)
    }
}

/**
 * Snapshot of game state for undo functionality.
 * Must be top-level to avoid serialization issues with Kotlin/JS.
 */
data class UndoSnapshot(
    val state: GameState,
    val handScores: Map<String, Int>,
    val prevHandSize: Int
)
